import { cn } from '~/lib/cn';

export interface StepDescriptor {
  key: string;
  title: string;
}

/**
 * Horizontal stepper for the KYB flow. The current step is black, completed
 * steps carry a check, and future steps are muted. The parent owns the
 * current-step index + the set of completed step keys; clicking a completed
 * step jumps back to it (users want to edit earlier sections mid-flow).
 */
export function Stepper({
  steps,
  current,
  completed,
  onJump,
}: {
  steps: StepDescriptor[];
  current: string;
  completed: Set<string>;
  onJump: (key: string) => void;
}): JSX.Element {
  return (
    <ol className="flex items-center gap-2">
      {steps.map((step, i) => {
        const isCurrent = step.key === current;
        const isDone = completed.has(step.key);
        const canJump = isDone && !isCurrent;
        return (
          <li key={step.key} className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={canJump ? () => onJump(step.key) : undefined}
              disabled={!canJump}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'group flex items-center gap-2 rounded-md px-2 py-1 text-left transition-colors',
                canJump && 'hover:bg-surface-margin',
                !canJump && 'cursor-default',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-5 w-5 flex-none items-center justify-center rounded-full text-[10px] font-semibold',
                  isCurrent && 'bg-ink-primary text-ink-on-dark',
                  isDone && !isCurrent && 'bg-tally text-ink-on-dark',
                  !isCurrent && !isDone && 'bg-surface-margin text-ink-watermark',
                )}
              >
                {isDone && !isCurrent ? (
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3.5 8.5l3 3 6-6" />
                  </svg>
                ) : (
                  i + 1
                )}
              </span>
              <span
                className={cn(
                  'truncate text-[13.5px] font-medium',
                  isCurrent ? 'text-ink-primary' : 'text-ink-secondary',
                )}
              >
                {step.title}
              </span>
            </button>
            {i < steps.length - 1 && (
              <span
                aria-hidden
                className={cn(
                  'h-px w-6 flex-none',
                  completed.has(step.key) ? 'bg-tally/60' : 'bg-rule-divider',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
